import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getProjects } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { JsonLd, useSEO } from '../utils/seo';
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const projects = getProjects();
  const project = projects.find(p => p.slug === slug);
  const activeIndex = projects.findIndex(p => p.slug === slug);
  // Every guide starts COLLAPSED. With one guide expanded on arrival, its long
  // detail panel pushed the remaining cards far off screen and the page looked
  // like a single project rather than a browsable index. The customer opens the
  // one they want; tapping it again closes it.
  const [isExpanded, setIsExpanded] = useState(false);
  // First render lands at the top of the page so the customer sees the guide
  // index and can orient. Every LATER switch scrolls to the detail panel that
  // just expanded — previously the page jumped to the top and the newly
  // selected guide rendered below all seven cards, so tapping a card looked
  // like nothing had happened.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      window.scrollTo(0, 0);
      return;
    }
    if (!isExpanded) return;
    const el = document.getElementById('project-detail');
    if (!el) {
      window.scrollTo(0, 0);
      return;
    }
    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - navHeight - 12,
      behavior: 'smooth'
    });
  }, [slug, isExpanded]);
  const projectDescription = project ? `${project.tagline} See recommended materials, depths, and practical tips for a ${project.name.toLowerCase()} project in the Puget Sound area.` : 'Explore practical gravel, drainage, patio, and landscaping project guides from 206Gravel.';
  useSEO({
    title: project ? `${project.name} Materials & Pro Tips | 206Gravel` : 'Gravel Project Guides | 206Gravel',
    description: projectDescription,
    path: project ? `/projects/${project.slug}` : '/'
  });
  if (!project) {
    return <Navigate to="/" replace data-id="element-3092" data-mp="3vcogz3zntr5w" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  return <main className="min-h-screen bg-[#f2f4f0] dark:bg-[#262626] pt-8 pb-24" data-id="element-3093" data-mp="2a04xfz9p11tg">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: project.name,
      description: projectDescription,
      url: `https://206gravel.com/projects/${project.slug}`,
      supply: project.materials.map(material => {
        const product = getProductDetails(material.slug);
        return {
          '@type': 'HowToSupply',
          name: product ? product.name : material.role,
          ...(product ? {
            url: `https://206gravel.com/shop/${product.slug}`,
            image: product.image
          } : {})
        };
      }),
      step: project.tips.map((tip, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: `Step ${index + 1}`,
        text: tip,
        url: `https://206gravel.com/projects/${project.slug}#step-${index + 1}`
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: project.faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: project.name,
        item: `https://206gravel.com/projects/${project.slug}`
      }]
    }]} data-id="element-3094" data-mp="2ey6debbtol0p" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3095" data-mp="37macx7736lom">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3096" data-mp="3cl3p8wan06n7">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3097" data-mp="2xocr0qcp90z6">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" data-id="element-3098" data-mp="32m4saaz8hzg1" />
          <span className="text-stone-900 dark:text-white font-medium" data-id="element-3099" data-mp="32p0nhrjp6a0s">
            {project.name}
          </span>
        </nav>

        {/* Project switcher — mirrors the Materials Hub: an accent rule, a
            large section heading, and a grid of photo-backed cards. The old
            icon-tile tabs read as navigation chrome; these read as browsable
            guides, which is what a customer who doesn't know their material is
            actually looking for. The photo comes from each guide's primary
            material, so the card shows the real thing being delivered. */}
        <section className="mb-14" aria-label="Project guides" data-id="element-3100" data-mp="e74i7eims2tb">
          <motion.div initial={{
          width: 0,
          opacity: 0
        }} whileInView={{
          width: 64,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-6 h-1 rounded-full bg-brand-orange" data-id="element-3101" data-mp="3mq3lv321663g" />
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-baseline md:gap-6" data-id="element-3102" data-mp="3qoj9n95xrihx">
            <h2 className="font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-brand-charcoal dark:text-white md:text-5xl" data-id="element-3103" data-mp="32z8ce1nso68j">
              Project <span className="font-black text-[#04A05C]" data-id="element-3104" data-mp="11zloe9ficxx9">Guides</span>
            </h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-3105" data-mp="16xes8vtc9fud">
              — Browse by project
            </span>
          </div>
          <p className="mb-8 max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-3106" data-mp="1bqq275tg6cqi">
            Pick the job you're doing and we'll show the exact materials, depths,
            and quantities that job needs.
          </p>

          <div className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 sm:gap-[26px] md:grid-cols-3" data-id="element-3107" data-mp="nzu3doqkqh9r">
            {projects.map((p, index) => {
            const isActive = p.slug === project.slug;
            const heroMaterial = getProductDetails(p.materials[0]?.slug ?? '');
            return <motion.div key={p.slug} style={{
              order: index * 2
            }} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              delay: Math.min(index, 5) * 0.035
            }} data-id="element-3108" data-mp="24c95903wc5ue" data-mpi={index}>
                  <Link to={`/projects/${p.slug}`} aria-current={isActive ? 'page' : undefined} aria-expanded={isActive ? isExpanded : false} onClick={() => setIsExpanded(prev => isActive ? !prev : true)} className={`group relative block h-[13.2rem] overflow-hidden rounded-lg border bg-stone-200 shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 hover:shadow-md sm:h-48 dark:bg-white/10 dark:focus-visible:ring-offset-[#262626] ${isActive && isExpanded ? 'border-[#04A05C] ring-2 ring-[#04A05C]' : 'border-stone-200 dark:border-white/10'}`} data-id="element-3109" data-mp="3oxpcc4maarq8">
                    {heroMaterial && <img src={heroMaterial.images?.[0] || heroMaterial.image} alt="" loading={index < 3 ? 'eager' : 'lazy'} decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-3110" data-mp="zexc46qyj2sf" />}
                    {isActive && isExpanded && <span className="absolute left-0 top-4 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#fffcf9] shadow-sm bg-[#006239]" data-id="element-3111" data-mp="88s1f7mf3kfv">
                        Open — tap to close
                      </span>}
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-[#FBFCFB] px-3 py-2.5 text-stone-650 sm:px-4 dark:bg-black/65 dark:text-white dark:backdrop-blur-[1px]" data-id="element-3112" data-mp="3unkye6u3y4i0">
                      <div className="min-w-0 font-sub" data-id="element-3113" data-mp="3lpj03z1rdpyf">
                        <p className="truncate text-[9px] uppercase tracking-[0.12em] text-stone-900 dark:text-white/80 font-[400] font-[Cabin,_sans-serif]" data-id="element-3114" data-mp="36qxe87160e2m">
                          Project guide
                        </p>
                        <h3 className="mt-0.5 truncate font-[Cabin,_sans-serif] text-[18px] font-[500] sm:text-base" data-id="element-3115" data-mp="150djksxd7dtg">
                          {p.name}
                        </h3>
                        <p className="mt-0.5 truncate text-[11px] leading-snug text-stone-600 dark:text-white/70 font-[Cabin,_sans-serif]" data-id="element-3116" data-mp="2ufwhsd8pors6">
                          {p.materials.length} {p.materials.length === 1 ? 'material' : 'materials'} · {p.typicalYards}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>;
          })}

            {/* The selected guide expands INSIDE the grid, as a full-width row
                ordered to fall immediately after its own card (cards take even
                orders, this panel takes the odd slot right after the active
                one). On mobile that means the content opens directly under the
                card the customer just tapped instead of below all seven. */}
            <div id="project-detail" hidden={!isExpanded} className="col-span-full scroll-mt-24" style={{
            order: activeIndex * 2 + 1
          }} data-id="element-3117" data-mp="1sqkihmykk21q">
        {/* Header */}
        <div className="mb-12" data-id="element-3118" data-mp="2vasd49kvwno">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3119" data-mp="2wc3f8qlgj5e">
            <Icon className="w-8 h-8" data-id="element-3120" data-mp="3u0ak6l2dixgi" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3121" data-mp="3p1qvwe0b59kz">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3122" data-mp="3l308iyln96b2">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3123" data-mp="gm783uieojdb">
            <p data-id="element-3124" data-mp="bv78ghhwjn74">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3125" data-mp="btw52isdchgj">
            <span className="mr-2" data-id="element-3126" data-mp="14e4kbmprpdey">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3127" data-mp="zk09uthamn94">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3128" data-mp="2n87be3r14g40">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3129" data-mp="2n9ihwbbv01yt">
            Recommended Materials
          </h2>
          <div className="space-y-6" data-id="element-3130" data-mp="2w8ckqjfs9okt">
            {project.materials.map((mat, idx) => {
                  const product = getProductDetails(mat.slug);
                  if (!product) return null;
                  return <motion.div key={mat.slug} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: idx * 0.1
                  }} className="bg-[#ffffff] dark:bg-[#303030] rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-white/10 flex flex-col md:flex-row gap-6 items-start" data-id="element-3131" data-mp="365qmdcx1s8t2" data-mpi={idx}>
                  <div className="w-full md:w-48 h-32 shrink-0 rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-800" data-id="element-3132" data-mp="3ovynjph6sp6m">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3133" data-mp="1ova83rgkg5lj" />
                  </div>
                  <div className="flex-1" data-id="element-3134" data-mp="1jtci8ednucjm">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-2" data-id="element-3135" data-mp="1ykldb30c0q45">
                      <div data-id="element-3136" data-mp="1tt3ae9er0fgj">
                        <div className="flex items-center gap-2 mb-1" data-id="element-3137" data-mp="1tstl6o1j2cbw">
                          <span className="text-xs font-bold uppercase tracking-wider text-brand-orange" data-id="element-3138" data-mp="27kgqfddg6v6t">
                            {mat.role}
                          </span>
                          <span className="text-stone-300 dark:text-stone-600" data-id="element-3139" data-mp="2hj5y6dbmqz9c">
                            •
                          </span>
                          <span className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-3140" data-mp="2cle9rta71v9w">
                            Depth: {mat.depth}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 dark:text-white" data-id="element-3141" data-mp="i86c7tuu35kk">
                          {product.name}
                        </h3>
                      </div>
                      <div className="text-right" data-id="element-3142" data-mp="2yrqfvqz77yl8">
                        <div className="text-lg font-bold text-stone-900 dark:text-white" data-id="element-3143" data-mp="2k45go85g2jil">
                          ${product.pricePerYard}
                          <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3144" data-mp="2p14467ulrs7t">
                            /yd
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-stone-600 dark:text-stone-300 mb-4" data-id="element-3145" data-mp="214zuj3ozdnwf">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3146" data-mp="21749cgof47bh">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>

                    <Link to={`/shop/${product.slug}`} className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-orange/80 transition-colors" data-id="element-3147" data-mp="4chjcbmiqoz9">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3148" data-mp="1weaj7g7729yg" />
                    </Link>
                  </div>
                </motion.div>;
                })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3149" data-mp="1rgr0a2zjx906">
          {/* Pro Tips */}
          <div data-id="element-3150" data-mp="1mhxocezwxq24">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3151" data-mp="1mi7j0fvwkle5">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#04A05C]" data-id="element-3152" data-mp="1cqa16qae2v6g" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3153" data-mp="18nwzccyrjhey">
              {project.tips.map((tip, idx) => <li key={idx} id={`step-${idx + 1}`} className="flex items-start scroll-mt-24" data-id="element-3154" data-mp="18m2eszrelrbe" data-mpi={idx}>
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3155" data-mp="13nioyo9ovvdr" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3156" data-mp="tr5w1slyxlk2">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3157" data-mp="om24zpix7llh">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3158" data-mp="olixl2qwcflp">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3159" data-mp="tmq4kzl7tarb" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3160" data-mp="yjzra7h7z8bg">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3161" data-mp="xixhc7p3cug" data-mpi={idx}>
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3162" data-mp="ykjgbq91jqy">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3163" data-mp="auxbz6a25rk4">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center bg-[#1a1a1a]" data-id="element-3164" data-mp="3ecfh35yg4kgu">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3165" data-mp="3ja78zzz3c6l1">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3166" data-mp="3b8gg3sl8b4al">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3167" data-mp="3kyswd8m0gbax">
            Get an Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3168" data-mp="2nc3polgd5aiq" />
          </Link>
        </div>
            </div>
          </div>
        </section>
      </div>
    </main>;
}